import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';

import { ListsAddEditModalComponent } from '@components/orders/saved-lists/lists-add-edit-modal/lists-add-edit-modal.component';
import { debounceTime, distinctUntilChanged, filter, startWith } from 'rxjs/operators';

import { FiltersAndPaginationPayloadService } from '../../@core/services/filters-and-pagination-payload.service';
import { OrdersActions } from '../../@store/orders/orders.actions';
import { selectOrderAssignees, selectOrderStatuses } from '../../@store/orders/orders.selectors';
import { CONFIG } from '../../config';
import { SafeAny } from '../../types/safe-any';

@Component({
  selector: 'ciwc-orders',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly payloadService = inject(FiltersAndPaginationPayloadService);
  private readonly dialog = inject(MatDialog);
  readonly assignees = this.store.selectSignal(selectOrderAssignees);
  readonly statuses = this.store.selectSignal(selectOrderStatuses);

  isSavedLists = signal(false);
  isEditPage = signal(false);

  currentTab = 0;
  searchControl = new FormControl('');

  // Filter controls
  statusFilterControl = new FormControl('');
  assigneeFilterControl = new FormControl('');
  fromDateControl = new FormControl('');
  toDateControl = new FormControl('');
  userFilterControl = new FormControl('');

  ngOnInit(): void {
    this.initializeCurrentTab();
    this.loadData();
    this.listenToFormChanges();
    this.listenToRouteChanges();
    this.listenToOrderSearchChanges();
    this.isEditPage.set(this.router.url.match(/\/orders\/[\w-]+\/edit$/) !== null);
  }

  ngOnDestroy(): void {
    this.resetFormControls();
  }

  onTabChange(index: number): void {
    this.currentTab = index;
    const routes = ['active-orders', 'archived-orders', 'all-orders', 'saved-lists'];

    this.router.navigate(['/orders', routes[index]]);
  }

  onClearFilters(): void {
    this.resetFormControls();

    this.store.dispatch(
      OrdersActions.loadOrdersList({
        payload: {
          ...this.payloadService.combinedPayload,
          archived: this.setCurrentTab(),
          q: this.payloadService.currentOrderSearchPayload,
        },
      }),
    );
  }

  private resetFormControls(): void {
    this.statusFilterControl.reset();
    this.assigneeFilterControl.reset();
    this.fromDateControl.reset();
    this.toDateControl.reset();
    this.userFilterControl.reset();
    this.payloadService.resetAll();
  }

  private loadData(): void {
    this.store.dispatch(OrdersActions.loadOrderAssignees({ payload: { category: 'ORDERS' } }));
    this.store.dispatch(
      OrdersActions.loadOrderStatuses({ payload: { identifier: 'ORDERS_STATUS' } }),
    );
  }

  private initializeCurrentTab(): void {
    switch (true) {
      case this.router.url.includes('archived-orders'):
        this.currentTab = 1;
        break;
      case this.router.url.includes('all-orders'):
        this.currentTab = 2;
        break;
      case this.router.url.includes('saved-lists'):
        this.currentTab = 3;

        // Set the saved list flag to true
        this.isSavedLists.set(true);

        break;
      default:
        this.currentTab = 0; // default to active-orders
    }
  }

  private listenToFormChanges(): void {
    // Setup search control subscription
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(400), distinctUntilChanged())
      .subscribe((value) => {
        this.payloadService.resetPagination();
        this.payloadService.updateOrderSearchPayload(value || '');
        this.payloadService.updateFilterPayload({ q: value || '' });
        this.store.dispatch(
          OrdersActions.loadOrdersList({
            payload: { ...this.payloadService.combinedPayload, archived: this.setCurrentTab() },
          }),
        );
      });

    // Handle status filter
    this.statusFilterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(null), distinctUntilChanged())
      .subscribe((status) => {
        if (!status) return;

        this.payloadService.resetPagination();
        this.payloadService.updateFilterPayload({ status });
        this.store.dispatch(
          OrdersActions.loadOrdersList({
            payload: { ...this.payloadService.combinedPayload, archived: this.setCurrentTab() },
          }),
        );
      });

    // Handle assignee filter
    this.assigneeFilterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(null), distinctUntilChanged())
      .subscribe((assignee) => {
        if (!assignee) return;

        this.payloadService.resetPagination();
        this.payloadService.updateFilterPayload({ assignee });
        this.store.dispatch(
          OrdersActions.loadOrdersList({
            payload: { ...this.payloadService.combinedPayload, archived: this.setCurrentTab() },
          }),
        );
      });

    // Handle date filter changes
    this.fromDateControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleDateFilters());

    this.toDateControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleDateFilters());

    // Handle user filter
    this.userFilterControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(null),
        filter((value: SafeAny): value is SafeAny => {
          if (!value) return false;
          return !!this.assignees()?.some((user: any) => user.id === +value);
        }),
        distinctUntilChanged(),
      )
      .subscribe((user) => {
        if (!user) return;

        this.payloadService.resetPagination();
        this.payloadService.updateFilterPayload({ user });
        this.store.dispatch(
          OrdersActions.loadOrdersList({
            payload: { ...this.payloadService.combinedPayload, archived: this.setCurrentTab() },
          }),
        );
      });
  }

  private handleDateFilters(): void {
    const fromDate = this.fromDateControl.value
      ? new DatePipe('en-US').transform(this.fromDateControl.value, CONFIG.date.formatUS)
      : null;
    const toDate = this.toDateControl.value
      ? new DatePipe('en-US').transform(this.toDateControl.value, CONFIG.date.formatUS)
      : null;

    if (fromDate || toDate) {
      this.payloadService.resetPagination();
      this.payloadService.updateFilterPayload({
        fromDate,
        toDate,
      });

      this.store.dispatch(
        OrdersActions.loadOrdersList({
          payload: { ...this.payloadService.combinedPayload, archived: this.setCurrentTab() },
        }),
      );
    }
  }

  private listenToRouteChanges(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      )
      .subscribe((event) => {
        this.isSavedLists.set(event.url.includes('orders/saved-lists'));
        this.isEditPage.set(event.url.match(/\/orders\/[\w-]+\/edit$/) !== null);
      });
  }

  openAddModal(): void {
    this.dialog.open(ListsAddEditModalComponent, {
      data: {
        isAdd: true,
        filtersForm: {
          statusFilter: this.statusFilterControl.value,
          assigneeFilter: this.assigneeFilterControl.value,
          fromDate: this.fromDateControl.value,
          toDate: this.toDateControl.value,
          userFilter: this.userFilterControl.value,
        },
      },
    });
  }

  private listenToOrderSearchChanges(): void {
    const currentSearchValue = this.payloadService.currentOrderSearchPayload;

    if (currentSearchValue) {
      this.searchControl.setValue(currentSearchValue);
    }
  }

  private setCurrentTab(): false | true | 'all' {
    switch (this.currentTab) {
      case 0:
        return false;
      case 1:
        return true;
      case 2:
        return 'all';
      default:
        return true;
    }
  }
}
